import React from "react";
import { Button, Dropdown, Select, Typography, Space } from "antd";
import type { MenuProps } from "antd";
import {
    PlusOutlined,
    DownOutlined,
    WomanOutlined,
} from "@ant-design/icons";
import { GiStethoscope } from "react-icons/gi";
import { FaBaby } from "react-icons/fa";
import { useIsMobile } from "../../../hooks/useIsMobile";

const { Option } = Select;
const { Text } = Typography;

const StethoscopeIcon = GiStethoscope as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const BabyIcon = FaBaby as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

type ModalType = "gynecologic" | "obstetric" | "medical" | null;

interface ExaminationHeaderProps {
    selectedType: string;
    onTypeChange: (value: string) => void;
    handleAddExamination: (type: ModalType) => void;
}

const ExaminationHeader: React.FC<ExaminationHeaderProps> = ({
    selectedType,
    onTypeChange,
    handleAddExamination,
}) => {
    const isMobile = useIsMobile();
    const menuItems: MenuProps["items"] = [
        {
            key: "gynecologic",
            label: (
                <span>
                    <WomanOutlined style={{ color: "#d46b08", marginRight: 8 }} />
                    Gynecologic Examination
                </span>
            ),
            onClick: () => handleAddExamination("gynecologic"),
        },
        {
            key: "obstetric",
            label: (
                <span>
                    <BabyIcon style={{ color: "#eb2f96", marginRight: 8 }} />
                    Obstetric Examination
                </span>
            ),
            onClick: () => handleAddExamination("obstetric"),
        },
        {
            key: "medical",
            label: (
                <span>
                    <StethoscopeIcon style={{ color: "#1677ff", marginRight: 8 }} />
                    Medical Consultation
                </span>
            ),
            onClick: () => handleAddExamination("medical"),
        },
    ];

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "stretch" : "center",
                gap: 12,
                background: "#fafafa",
                borderRadius: 12,
                padding: isMobile ? "12px 14px" : "16px 20px",
                marginBottom: 20,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
        >
            <Space direction="vertical" size={0} style={{ flex: "1 1 auto", minWidth: isMobile ? 0 : 240, width: isMobile ? "100%" : undefined }}>
                <Text
                    style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#6b7280",
                        marginBottom: 4,
                    }}
                >
                    Filter Examinations
                </Text>
                <Select
                    value={selectedType}
                    onChange={onTypeChange}
                    style={{
                        width: "100%",
                        maxWidth: isMobile ? "100%" : 300,
                        borderRadius: 8,
                    }}
                    placeholder="Select examination type"
                >
                    <Option value="gynecologic">
                        <WomanOutlined style={{ color: "#d46b08", marginRight: 8 }} />
                        Gynecologic Examinations
                    </Option>
                    <Option value="obstetric">
                        <BabyIcon style={{ color: "#eb2f96", marginRight: 8 }} />
                        Obstetric Examinations
                    </Option>
                    <Option value="medical">
                        <StethoscopeIcon style={{ color: "#1677ff", marginRight: 8 }} />
                        Medical Consultations
                    </Option>
                </Select>
            </Space>

            {/* Right side: Add Examination */}
            <Dropdown menu={{ items: menuItems }} placement="bottomRight">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block={isMobile}
                    style={{
                        borderRadius: 8,
                        padding: "8px 20px",
                        fontWeight: 500,
                        background: "linear-gradient(90deg, #1677ff 0%, #4096ff 100%)",
                        boxShadow: "0 4px 10px rgba(22,119,255,0.2)",
                    }}
                >
                    Add Examination
                    <DownOutlined style={{ fontSize: 10, marginLeft: 8 }} />
                </Button>
            </Dropdown>
        </div>
    );
};

export default ExaminationHeader;
